import type { KnowledgeParticle } from '../types/domain';
import type { EmbedImageKind } from '../utils/geometryVendorKinds';
import { apiClient } from './apiClient';

export type DajiaoGeometryRegenResult = {
  taskId: string;
  model: string;
  artifactUrl: string;
  pngUrl: string;
  elapsedMs: number;
  width?: number;
  height?: number;
  particleId?: string | null;
};

export type GeometryCandidateId =
  | 'original'
  | 'dajiao'
  | 'dajiao-qwen-agent'
  | 'gpt-image-2'
  | 'qwen-image-3.0'
  | 'qwen-image-3.0-pro';
export type GeometryGeneratorId = Exclude<GeometryCandidateId, 'original'>;

export type GeometryCandidate = {
  id: GeometryCandidateId;
  label: string;
  model: string;
  provider: string;
  pngUrl: string | null;
  width?: number;
  height?: number;
  artifactUrl?: string | null;
  costUsd: number | null;
  costCny: number | null;
  costEstimated: boolean;
  elapsedMs: number;
  error?: string | null;
  assetId?: string | null;
  ossId?: string | null;
  persistedAt?: string | null;
};

export type GeometryCandidatesResult = {
  referenceImageUrl: string;
  /** 落库后的候选槽；失败时可能仍带着旧成功图 */
  candidates: GeometryCandidate[];
  /** 本次请求原始结果；失败时 pngUrl 为空且带 error */
  attempts?: GeometryCandidate[];
  particle?: KnowledgeParticle;
  particleId?: string;
};

export type DajiaoAdoptResult = {
  particle: KnowledgeParticle;
  active: 'original' | 'generated';
  asset?: { id: string; oss_id: string; url: string };
};

export type EmbedImageClassifyResult = {
  result: {
    kind: EmbedImageKind;
    isGeometry: boolean;
    confidence: number;
    reason: string;
    model: string;
    classifiedAt: string;
  };
  particle: KnowledgeParticle;
};

export const dajiaoService = {
  regenerateGeometry(input: {
    particleId?: string;
    imageUrl?: string;
    ossId?: string;
    content?: string;
    model?: string;
  }): Promise<DajiaoGeometryRegenResult> {
    return apiClient.postEnvelope<DajiaoGeometryRegenResult>(
      '/api/dajiao/regenerate-geometry',
      input,
      true,
      { timeoutMs: 240_000 },
    );
  },

  generateCandidates(input: {
    particleId?: string;
    imageUrl?: string;
    /** 精确切块 data URL（优先于 imageUrl，防串题） */
    imageDataUrl?: string;
    ossId?: string;
    /** 题干预览，供 NGT 清脏补结构 */
    questionHint?: string;
    /** 工厂插图键：batchId:unitId:qaIndex → 建/找 KP 并写入嵌入槽 */
    factoryFigureKey?: string;
    /** 页图切块参数（相对整页 + 实际采用框），入库备查 / 服务端可复切 */
    cropMeta?: {
      /** 提取原始 figure_box（相对整页 0–1） */
      figureBox: { x: number; y: number; w: number; h: number };
      /** 实际切图框（扩边后，相对整页 0–1） */
      boxUsed: { x: number; y: number; w: number; h: number };
      /** 像素框（相对页图像素） */
      cropPixels: { x: number; y: number; w: number; h: number };
      pageWidth: number;
      pageHeight: number;
    };
    /** 指定模型；空则服务端跑默认列表（含大角 / Qwen Agent / Image2 等） */
    models?: GeometryGeneratorId[];
  }): Promise<GeometryCandidatesResult> {
    return apiClient.postEnvelope<GeometryCandidatesResult>(
      '/api/dajiao/generate-candidates',
      input,
      true,
      { timeoutMs: 600_000 },
    );
  },

  /** 回显工厂插图试跑已落库的 OI2/QI3（不生图） */
  getFactoryFigure(factoryFigureKey: string): Promise<{
    particleId: string | null;
    candidates: GeometryCandidate[];
  }> {
    const key = encodeURIComponent(String(factoryFigureKey || '').trim());
    return apiClient.getEnvelope<{
      particleId: string | null;
      candidates: GeometryCandidate[];
    }>(`/api/dajiao/factory-figure?key=${key}`, true);
  },

  classifyEmbedImage(input: {
    particleId: string;
    ossId?: string;
  }): Promise<EmbedImageClassifyResult> {
    return apiClient.postEnvelope<EmbedImageClassifyResult>(
      '/api/dajiao/classify-embed-image',
      input,
      true,
      { timeoutMs: 120_000 },
    );
  },

  adoptGenerated(input: {
    particleId: string;
    pngUrl?: string;
    ossId?: string;
    assetId?: string;
    width?: number;
    height?: number;
    model?: string;
    candidateId?: GeometryGeneratorId;
    artifactUrl?: string;
  }): Promise<DajiaoAdoptResult> {
    return apiClient.postEnvelope<DajiaoAdoptResult>(
      '/api/dajiao/adopt-generated',
      input,
      true,
      { timeoutMs: 120_000 },
    );
  },

  adoptOriginal(input: { particleId: string }): Promise<DajiaoAdoptResult> {
    return apiClient.postEnvelope<DajiaoAdoptResult>(
      '/api/dajiao/adopt-original',
      input,
      true,
      { timeoutMs: 60_000 },
    );
  },
};
